import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FilterSelect from '@/components/ui/filter-select';
import { Label } from '@/components/ui/label';
import { useApi } from '@/contexts/ApiContext';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';

// Types
interface SourcingOfficerFormData {
  officerId: string;
  sourcingState: string;
}

interface User {
  _id: string;
  username: string;
  email: string;
  role:
    | {
        _id: string;
        name: string;
      }
    | string;
}

interface SourcingOfficerCreateSheetProps {
  regionId: string;
  regionName: string;
  onOfficerAdded?: () => Promise<void>;
}

const SourcingOfficerCreateSheet: React.FC<SourcingOfficerCreateSheetProps> = ({
  regionId,
  regionName,
  onOfficerAdded,
}) => {
  const api = useApi();
  const { hideSheet } = useGlobalState();
  const { showSuccess, showError } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SourcingOfficerFormData>({
    defaultValues: {
      officerId: '',
      sourcingState: '',
    },
  });

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const response = await api.get('/users');
        if (response.success) {
          setUsers(response.data?.users || []);
        } else {
          showError('Failed to fetch users');
        }
      } catch {
        showError('An error occurred while fetching users');
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [api, showError]);

  const onSubmit = async (formData: SourcingOfficerFormData) => {
    setIsLoading(true);
    try {
      const response = await api.put(`/region/${regionId}`, {
        regionName: regionName,
        sourcingOfficers: [
          {
            _id: formData.officerId,
          },
        ],
        sourcingState: formData.sourcingState,
      });

      if (response.success) {
        showSuccess(response.message || 'Sourcing officer added successfully');
        reset();
        hideSheet('sourcing-officer-create');

        // Refresh the region data if callback provided
        if (onOfficerAdded) {
          await onOfficerAdded();
        }
      } else {
        showError(response.error?.message || 'Failed to add sourcing officer');
      }
    } catch {
      showError('An error occurred while adding the sourcing officer');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    reset();
    hideSheet('sourcing-officer-create');
  };

  // Expose handlers to window object for external button access
  useEffect(() => {
    (window as any).sourcingOfficerCreateHandlers = {
      handleAddOfficer: () => handleSubmit(onSubmit)(),
      handleCancel,
      isLoading,
      hasErrors: Object.keys(errors).length > 0,
    };

    return () => {
      delete (window as any).sourcingOfficerCreateHandlers;
    };
  }, [handleSubmit, onSubmit, handleCancel, isLoading, errors]);

  // User options for sourcing officer selection
  const userOptions = users.map(user => {
    const roleName = typeof user.role === 'object' ? user.role.name : user.role;
    return {
      value: user._id,
      label: `${user.username} (${user.email}) - ${roleName}`,
    };
  });

  // Nigerian states options
  const stateOptions = [
    { value: 'abia', label: 'Abia' },
    { value: 'adamawa', label: 'Adamawa' },
    { value: 'akwa-ibom', label: 'Akwa Ibom' },
    { value: 'anambra', label: 'Anambra' },
    { value: 'bauchi', label: 'Bauchi' },
    { value: 'bayelsa', label: 'Bayelsa' },
    { value: 'benue', label: 'Benue' },
    { value: 'borno', label: 'Borno' },
    { value: 'cross-river', label: 'Cross River' },
    { value: 'delta', label: 'Delta' },
    { value: 'ebonyi', label: 'Ebonyi' },
    { value: 'edo', label: 'Edo' },
    { value: 'ekiti', label: 'Ekiti' },
    { value: 'enugu', label: 'Enugu' },
    { value: 'gombe', label: 'Gombe' },
    { value: 'imo', label: 'Imo' },
    { value: 'jigawa', label: 'Jigawa' },
    { value: 'kaduna', label: 'Kaduna' },
    { value: 'kano', label: 'Kano' },
    { value: 'katsina', label: 'Katsina' },
    { value: 'kebbi', label: 'Kebbi' },
    { value: 'kogi', label: 'Kogi' },
    { value: 'kwara', label: 'Kwara' },
    { value: 'lagos', label: 'Lagos' },
    { value: 'nasarawa', label: 'Nasarawa' },
    { value: 'niger', label: 'Niger' },
    { value: 'ogun', label: 'Ogun' },
    { value: 'ondo', label: 'Ondo' },
    { value: 'osun', label: 'Osun' },
    { value: 'oyo', label: 'Oyo' },
    { value: 'plateau', label: 'Plateau' },
    { value: 'rivers', label: 'Rivers' },
    { value: 'sokoto', label: 'Sokoto' },
    { value: 'taraba', label: 'Taraba' },
    { value: 'yobe', label: 'Yobe' },
    { value: 'zamfara', label: 'Zamfara' },
    { value: 'fct', label: 'Federal Capital Territory (FCT)' },
  ];

  return (
    <div className='space-y-6'>
      <div className='space-y-4'>
        {/* Sourcing Officer */}
        <div className='space-y-2'>
          <Label htmlFor='officerId'>Sourcing Officer</Label>
          <Controller
            name='officerId'
            control={control}
            rules={{ required: 'Please select a sourcing officer' }}
            render={({ field, fieldState }) => (
              <FilterSelect
                name='officerId'
                value={field.value}
                options={userOptions}
                placeholder='select sourcing officer'
                onChange={field.onChange}
                className='w-full'
                error={fieldState?.error?.message}
                disabled={isLoadingUsers}
              />
            )}
          />
          {isLoadingUsers && (
            <p className='text-sm text-gray-50'>Loading users...</p>
          )}
        </div>

        {/* Sourcing State */}
        <div className='space-y-2'>
          <Label htmlFor='sourcingState'>Sourcing State</Label>
          <Controller
            name='sourcingState'
            control={control}
            rules={{ required: 'Please select a sourcing state' }}
            render={({ field, fieldState }) => (
              <FilterSelect
                name='sourcingState'
                value={field.value}
                options={stateOptions}
                placeholder='Select sourcing state'
                onChange={field.onChange}
                className='w-full'
                error={fieldState?.error?.message}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default SourcingOfficerCreateSheet;
